import React from "react";
import { Link } from "react-router-dom";

const pageStyle = { padding: "2rem", paddingTop: "8rem", maxWidth: "900px", margin: "0 auto" };

const EnglishTerms = () => (
  <div style={pageStyle}>
    <h1>Terms of Use for Tomko</h1>
    <p><strong>Last updated:</strong> May 2026</p>

    <p>
      These Terms of Use (&quot;Terms&quot;) govern your access to and use of the Tomko mobile application
      and related services (&quot;Tomko&quot;, &quot;Service&quot;) operated by Gobicraft Systems LLC
      (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). By creating an account,
      signing in, or using Tomko, you agree to these Terms. If you do not agree, do not use the Service.
    </p>

    <p>
      These Terms apply together with our{" "}
      <Link to="/privacy">Privacy Policy</Link>, which explains how we collect and use personal information.
      Subscription terms for Tomko Plus are described in our{" "}
      <Link to="/tomko-plus-terms">Tomko Plus Terms of Use</Link>.
    </p>

    <h2>1. The Service</h2>
    <p>
      Tomko is a language-learning application that provides interactive lessons, exercises, and related
      educational content. Features may change over time. We may add, modify, suspend, or discontinue any
      part of the Service at our discretion, with or without notice, where permitted by law.
    </p>

    <h2>2. Eligibility and Accounts</h2>
    <p>
      You are responsible for the accuracy of information you provide when registering. You must keep your
      login credentials secure and notify us if you believe your account has been compromised.
    </p>
    <p>You may register or sign in using:</p>
    <ul>
      <li>a mobile phone number;</li>
      <li>an email address; or</li>
      <li>Sign in with Google or Sign in with Apple.</li>
    </ul>

    <h3>2.1 Sign in with Google or Apple</h3>
    <p>
      If you use Sign in with Google or Sign in with Apple, we only require your <strong>email address</strong>,{" "}
      <strong>first name</strong>, and <strong>last name</strong> to create or access your Tomko account. We
      use that information only to authenticate you and provide the Service. Further details are in our{" "}
      <Link to="/privacy">Privacy Policy</Link>.
    </p>

    <h2>3. Acceptable Use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>use Tomko in violation of any applicable law or regulation;</li>
      <li>attempt to gain unauthorized access to accounts, systems, or data;</li>
      <li>interfere with or disrupt the Service, including by automated means not authorized by us;</li>
      <li>copy, modify, reverse engineer, or distribute the Service except as allowed by law;</li>
      <li>use the Service to harass, abuse, or harm others, or to distribute unlawful content.</li>
    </ul>

    <h2>4. Tomko Plus and In-App Purchases</h2>
    <p>
      Tomko Plus is an optional paid subscription available through Google Play Store or Apple App Store.
      Prices, billing periods, auto-renewal, cancellation, trials, and refunds are governed by the
      applicable app store and our <Link to="/tomko-plus-terms">Tomko Plus Terms of Use</Link>. We do not
      directly process payment card or banking details for app store purchases.
    </p>
    <p>
      Unless required by applicable law or the relevant app store policy, subscription fees are non-refundable
      by the Company once processed through the app store.
    </p>

    <h2>5. Intellectual Property</h2>
    <p>
      Tomko, including its software, design, text, graphics, lessons, and branding, is owned by the Company
      or its licensors and is protected by applicable intellectual property laws. Subject to these Terms, we
      grant you a limited, non-exclusive, non-transferable, revocable license to use Tomko for personal,
      non-commercial learning purposes.
    </p>

    <h2>6. Disclaimers</h2>
    <p>
      Tomko is provided on an &quot;as is&quot; and &quot;as available&quot; basis. To the fullest extent
      permitted by law, we disclaim all warranties, express or implied, including implied warranties of
      merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the
      Service will be uninterrupted, error-free, or that learning outcomes will meet any particular goal.
    </p>
    <p>
      Tomko is an educational tool and does not constitute professional, legal, medical, or other specialized
      advice.
    </p>

    <h2>7. Limitation of Liability</h2>
    <p>
      To the fullest extent permitted by applicable law, the Company and its affiliates, officers, directors,
      employees, and suppliers will not be liable for any indirect, incidental, special, consequential, or
      punitive damages, or for loss of profits, data, or goodwill, arising from or related to your use of
      Tomko, even if we have been advised of the possibility of such damages.
    </p>

    <h2>8. Termination</h2>
    <p>
      You may stop using Tomko at any time and may delete your account in the app. We may suspend or terminate
      your access if you breach these Terms or if we reasonably believe your use poses a risk to the Service,
      other users, or the Company.
    </p>

    <h2>9. Children</h2>
    <p>
      Tomko is not directed to children under 4, and we do not knowingly allow such children to register
      without appropriate consent where required by law. If you believe a child has provided information in
      violation of our policies, contact us using the details below.
    </p>

    <h2>10. Governing Law</h2>
    <p>
      These Terms are governed by the laws of Mongolia, without regard to conflict-of-law principles. Disputes
      arising from these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of
      Mongolia, unless mandatory local law provides otherwise.
    </p>

    <h2>11. Changes to These Terms</h2>
    <p>
      We may update these Terms from time to time. We will post the revised Terms on this page and update the
      &quot;Last updated&quot; date. Your continued use of Tomko after changes become effective constitutes
      acceptance of the revised Terms, where permitted by law.
    </p>

    <h2>12. Contact Us</h2>
    <p>If you have questions about these Terms, contact us:</p>
    <ul>
      <li>
        Facebook:{" "}
        <a href="https://www.facebook.com/tomkomn/" target="_blank" rel="noopener noreferrer">
          Tomko Mongolia
        </a>
      </li>
      <li>Email: contact@gobicraft.com</li>
      <li>Company: Gobicraft Systems LLC</li>
    </ul>
  </div>
);

const MongolianTerms = () => (
  <div style={pageStyle}>
    <h1>Tomko үйлчилгээний нөхцөл</h1>
    <p><strong>Сүүлд шинэчилсэн:</strong> 2026 оны 5 дугаар сар</p>

    <p>
      Энэхүү Үйлчилгээний нөхцөл (&quot;Нөхцөл&quot;) нь Гобикрафт Системс ХХК (&quot;Компани&quot;, &quot;Бид&quot;)
      ажиллуулж буй Tomko гар утасны аппликейшн болон түүнтэй холбоотой үйлчилгээг (&quot;Tomko&quot;,
      &quot;Үйлчилгээ&quot;) ашиглахтай холбоотой харилцааг зохицуулна. Та бүртгэл үүсгэх, нэвтрэх, эсвэл
      Tomko ашигласнаар эдгээр Нөхцөлийг хүлээн зөвшөөрсөнд тооцогдоно. Хэрэв та зөвшөөрөхгүй бол Үйлчилгээг
      ашиглахгүй байна уу.
    </p>

    <p>
      Эдгээр Нөхцөл нь манай{" "}
      <Link to="/privacy">Нууцлалын бодлого</Link>-той хамт хэрэгжинэ. Tomko Plus захиалгын нөхцөлийг{" "}
      <Link to="/tomko-plus-terms">Tomko Plus үйлчилгээний нөхцөл</Link>-өөс үзнэ үү.
    </p>

    <h2>1. Үйлчилгээ</h2>
    <p>
      Tomko нь интерактив хичээл, дасгал, болон холбогдох сургалтын контентоор гадаад хэл суралцахад зориулсан
      аппликейшн юм. Бид хуульд нийцсэн хэмжээнд үйлчилгээний аль нэг хэсгийг нэмэх, өөрчлөх, түр зогсоох,
      эсвэл зогсоох эрхтэй.
    </p>

    <h2>2. Эрх зүйн болон бүртгэл</h2>
    <p>
      Та бүртгэлд өгсөн мэдээллийн үнэн зөв байдлыг хариуцна. Нэвтрэх эрхээ нууцлах, бүртгэлээ бусдад
      ашиглуулахгүй байх үүрэгтэй.
    </p>
    <p>Та дараах аргаар бүртгүүлж, нэвтэрч болно:</p>
    <ul>
      <li>гар утасны дугаар;</li>
      <li>имэйл хаяг; эсвэл</li>
      <li>Google эсвэл Apple-ээр нэвтрэх.</li>
    </ul>

    <h3>2.1 Google эсвэл Apple-ээр нэвтрэх</h3>
    <p>
      Google эсвэл Apple-ээр нэвтрэх үед бид Tomko бүртгэл үүсгэх, нэвтрэхэд зөвхөн таны{" "}
      <strong>имэйл хаяг</strong>, <strong>нэр</strong>, <strong>овог</strong>-ийг ашиглана. Энэ мэдээллийг
      зөвхөн таныг баталгаажуулах, үйлчилгээ үзүүлэхэд ашиглана. Дэлгэрэнгүйг{" "}
      <Link to="/privacy">Нууцлалын бодлого</Link>-оос үзнэ үү.
    </p>

    <h2>3. Хэрэглээний дүрэм</h2>
    <p>Та дараахыг хийхгүй байхыг зөвшөөрнө:</p>
    <ul>
      <li>холбогдох хууль, журмыг зөрчих;</li>
      <li>зөвшөөрөлгүйгээр бүртгэл, систем, мэдээлэлд нэвтрэхийг оролдох;</li>
      <li>үйлчилгээг саатуулах, тасалдуулах, эсвэл зөвшөөрөлгүй автомат хэрэгслээр ашиглах;</li>
      <li>хуулиар зөвшөөрөгдсөнээс бусад тохиолдолд программыг хуулбарлах, өөрчлөх, эсвэл тараах;</li>
      <li>бусдыг хүндэтгэхгүй, хор хөнөөл учруулах, эсвэл хууль бус контент түгээх.</li>
    </ul>

    <h2>4. Tomko Plus болон апп доторх худалдан авалт</h2>
    <p>
      Tomko Plus нь Google Play Store эсвэл Apple App Store-оор худалдан авах сонголттой захиалгын үйлчилгээ
      юм. Үнэ, төлбөрийн хугацаа, автомат сунгалт, цуцлалт, туршилт, буцаан олголтыг холбогдох app store болон
      манай <Link to="/tomko-plus-terms">Tomko Plus үйлчилгээний нөхцөл</Link> зохицуулна. App store-оор
      хийсэн төлбөрийг Компани шууд боловсруулдаггүй.
    </p>
    <p>
      Холбогдох хууль, app store-ийн бодлогоос бусад тохиолдолд захиалгын төлбөр нь Компаниас буцаан
      олгогдохгүй.
    </p>

    <h2>5. Оюуны өмч</h2>
    <p>
      Tomko-ийн програм, дизайн, текст, график, хичээл, брэнд зэрэг нь Компани эсвэл лиценз өгөгчдийн өмч
      бөгөөд холбогдох хуулиар хамгаалагдсан. Эдгээр Нөхцөлийн дагуу бид танд хувийн, арилжааны бус сургалтын
      зорилгоор Tomko ашиглах хязгаарлагдмал, шилжүүлэх боломжгүй эрх олгоно.
    </p>

    <h2>6. Хариуцлагаас татгалзах</h2>
    <p>
      Tomko нь &quot;байгаагаар&quot; болон &quot;боломжит байгаа&quot; зарчмаар үзүүлэгддэг. Хуульд зөвшөөрөгдсөн
      хэмжээнд бид шууд болон далд баталгаа, түүний дотор худалдаалах боломж, тодорхой зорилгод тохирох байдал,
      эрх зөрчилгүй байдлын баталгааг үгүйсгэнэ. Үйлчилгээ үргэлж саадгүй, алдаагүй ажиллана гэдгийг бид
      баталгаажуулахгүй.
    </p>

    <h2>7. Хариуцлагын хязгаарлалт</h2>
    <p>
      Хуульд зөвшөөрөгдсөн хэмжээнд Компани болон түүнтэй холбоотой этгээдүүд Tomko ашиглалтаас үүдэх шууд
      бус, санамсаргүй, онцгой, үр дагаврын хохирол, ашиг, өгөгдөл алдах хариуцлага хүлээхгүй.
    </p>

    <h2>8. Үйлчилгээг зогсоох</h2>
    <p>
      Та хүссэн үедээ Tomko ашиглахаа зогсоож, апп дотор бүртгэлээ устгаж болно. Та эдгээр Нөхцөлийг зөрчсөн,
      эсвэл үйлчилгээ, бусад хэрэглэгч, Компанид эрсдэл учруулж байна гэж үндэслэлтэй үндэслэлээр бид таны
      хандалтыг түр зогсоох, эсвэл бүрмөсөн хаах эрхтэй.
    </p>

    <h2>9. Хүүхэд</h2>
    <p>
      Tomko нь 4 нас хүрээгүй хүүхдэд зориулагдаагүй. Хуульд шаардлагатай тохиолдолд зөвшөөрөлгүй бүртгэл
      илэрвэл бидэнтэй холбогдоно уу.
    </p>

    <h2>10. Хэрэглэх хууль</h2>
    <p>
      Эдгээр Нөхцөлд Монгол улсын хууль үйлчилнэ. Маргааныг Монгол улсын шүүхээр шийдвэрлүүлнэ, хэрэв
      заавал мөрдөх орон нутгийн хууль өөрөөр заагаагүй бол.
    </p>

    <h2>11. Нөхцөлийн өөрчлөлт</h2>
    <p>
      Бид эдгээр Нөхцөлийг үе үе шинэчилж болно. Шинэчилсэн хувилбарыг энэ хуудсанд нийтэлж, &quot;Сүүлд
      шинэчилсэн&quot; огноог өөрчилнө. Өөрчлөлт хүчин төгөлдөр болсны дараа Tomko ашиглассаар байвал та
      шинэчилсэн Нөхцөлийг хүлээн зөвшөөрсөнд тооцогдоно.
    </p>

    <h2>12. Холбоо барих</h2>
    <p>Эдгээр Нөхцөлтэй холбоотой асуултаар холбогдоно уу:</p>
    <ul>
      <li>
        Facebook:{" "}
        <a href="https://www.facebook.com/tomkomn/" target="_blank" rel="noopener noreferrer">
          Tomko Learn a Language
        </a>
      </li>
      <li>Имэйл: contact@gobicraft.com</li>
      <li>Компани: Гобикрафт Системс ХХК</li>
    </ul>
  </div>
);

const Terms = ({ language = "en" }) => {
  return language === "mn" ? <MongolianTerms /> : <EnglishTerms />;
};

export default Terms;
